import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { queryApi } from 'app/apis/queryApi';
import { RootState } from 'app/store';
import { logout } from 'features/auth/authenticationSlice';
import { clearSites, selectSite } from 'features/topbar/selectedSiteSlice';
import { PURGE } from 'redux-persist';

export interface SettingsState {
  currencyCode: string;
  reportableCurrencies: string[];
}

export const HARDCODED_CURRENCIES = [
  'USD',
  'GBP',
  'EUR',
  'CAD',
  'NZD',
  'SGD',
  'ZAR',
  'AUD',
  'IDR',
  'BRL',
  'MXN',
  'AED',
  'PLN',
  'INR',
  'TRY',
  'ILS',
  'CNY',
  'CZK',
  'DKK',
  'HKD',
  'JPY'
];

export const CURRENCY_LABELS: Record<string, string> = {
  USD: 'United States Dollars',
  GBP: 'British Pounds',
  EUR: 'Euros',
  CAD: 'Canadian Dollars',
  NZD: 'New Zealand Dollars',
  SGD: 'Singapore Dollars',
  ZAR: 'South African Rand',
  AUD: 'Australian Dollars',
  IDR: 'Indonesian Rupiah',
  BRL: 'Brazilian Real',
  MXN: 'Mexican Peso',
  AED: 'United Arab Emirates Dirham',
  PLN: 'Polish Złoty',
  INR: 'Indian Rupee',
  TRY: 'Turkish Lira',
  ILS: 'Israeli New Shekel',
  CNY: 'Chinese Yuan',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  HKD: 'Hong Kong Dollars',
  JPY: 'Japanese Yen'
};

const initialState: SettingsState = {
  currencyCode: 'GBP',
  reportableCurrencies: HARDCODED_CURRENCIES
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setCurrencyCode: (state, action: PayloadAction<string>) => {
      state.currencyCode = action.payload;
    },
    setReportableCurrencies: (state, action: PayloadAction<string[]>) => {
      state.reportableCurrencies = action.payload;
    },
    resetReportableCurrencies: (state) => {
      state.reportableCurrencies = HARDCODED_CURRENCIES;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addCase(selectSite, () => initialState);
    builder.addCase(logout, () => initialState);
    builder.addCase(clearSites, () => initialState);

    builder.addMatcher(
      queryApi.endpoints.getSiteCurrencies.matchFulfilled,
      (state, { payload }) => {
        state.reportableCurrencies = payload;
      }
    );
  }
});

export const {
  setCurrencyCode,
  setReportableCurrencies,
  resetReportableCurrencies
} = settingsSlice.actions;

export const selectCurrencyCode = createSelector(
  (state: RootState) => state.settings,
  (settings) => settings.currencyCode
);

export const selectReportableCurrencies = createSelector(
  (state: RootState) => state.settings,
  (settings) => settings.reportableCurrencies
);

export const selectCurrencyLabel = (code: string) =>
  CURRENCY_LABELS[code] ? `${CURRENCY_LABELS[code]} (${code})` : code;

export const selectAllCurrencyLabels = () => CURRENCY_LABELS;

export default settingsSlice.reducer;
